import { Link, Text } from '@alinta-digital/asg';
import styled from 'styled-components';

/**
 * Concat given property names with className if the properties value is truthy.
 * @param props properties of a StyledComponent
 * @param propNames property names to be used as css classes if the property value is truthy
 * @returns css class names as string
 */
export function generateClassNameByPropNames(
  props: Record<string, any>,
  ...propNames: string[]
): string {
  let className = '';
  for (const propName of propNames) {
    if (!props[propName]) {
      continue;
    }
    className += `${propName} `;
  }
  return className + (props.className ?? '');
}
const btnColors = {
  background: 'white',
  secondary: '#196cff',
  secondaryDisabled: '#9fa6ac',
  secondaryActive: '#004fdc',
  secondaryHover: '#004bcc',
};

export interface ButtonV2Props {
  /** primary button theme */
  primary?: boolean;
  /** secondary button theme */
  secondary?: boolean;
  /** show load spinner and disable clicks */
  loading?: boolean;
}

export const StyledLink = styled(Link)`
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  color: #196cff !important;
`;

export const StyledText = styled(Text)`
  color: #000f20 !important;
  font-weight: 400;
`;

export const StyledTextThanks = styled(Text)`
  color: #000f20 !important;
  font-weight: 400;
  display: 'flex';
  line-height: 22px;
`;
/** Button with primary/secondary styling.
 */
export const ButtonV2 = styled.button.attrs<ButtonV2Props>((props) => ({
  className: generateClassNameByPropNames(
    props,
    'primary',
    'secondary',
    'loading'
  ),
  'aria-busy': props.loading ?? false,
}))<ButtonV2Props>`
  height: 48px;
  padding: 12px 24px 12px 24px;
  color: ${btnColors.background};
  text-align: center;
  font-size: 18px;
  vertical-align: middle;
  border: none;
  border-radius: 24px;
  transition: background-color 0.1s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  font-weight: 500;

  @keyframes circle__svg {
    0% {
      transform: rotateZ(0deg);
    }
    100% {
      transform: rotateZ(360deg);
    }
  }

  &.primary {
    &:enabled {
      background-color: ${btnColors.secondary};
    }

    &:disabled {
      background-color: ${btnColors.secondaryDisabled};
    }

    &:not(:disabled) {
      cursor: pointer;

      &:hover {
        color: ${btnColors.background};
        background-color: ${btnColors.secondaryHover};
      }

      &:active {
        color: ${btnColors.background};
        background-color: ${btnColors.secondaryActive};
      }
    }
  }

  &.secondary {
    &:enabled {
      color: ${btnColors.secondary};
      background-color: ${btnColors.background};
      border: 2px solid ${btnColors.secondary};
    }

    &:disabled {
      color: ${btnColors.secondaryDisabled};
      background-color: ${btnColors.background};
      border: 2px solid ${btnColors.secondaryDisabled};
    }

    &:not(:disabled) {
      cursor: pointer;

      &:hover {
        color: ${btnColors.background};
        background-color: ${btnColors.secondaryHover};
        border-color: ${btnColors.secondaryHover};
      }

      &:active {
        color: ${btnColors.background};
        background-color: ${btnColors.secondaryActive};
        border-color: ${btnColors.secondaryActive};
      }
    }
  }

  &[aria-busy='true'] {
    pointer-events: none;
    user-select: none;

    ::before {
      animation: circle__svg 0.75s linear infinite;
      transform: translateY(-50%);
      width: 25px;
      height: 25px;
      content: url("data:image/svg+xml,%3Csvg viewBox='-25 -25 250 250' version='1.1' xmlns='http://www.w3.org/2000/svg' style='transform:rotate(-90deg)'%3E%3Ccircle r='90' cx='100' cy='100' fill='transparent' stroke='%23ffffff' stroke-width='16px' stroke-dasharray='565.48px' stroke-dashoffset='0'%3E%3C/circle%3E%3Ccircle r='90' cx='100' cy='100' stroke='%23196cff' stroke-width='10' stroke-linecap='round' stroke-dashoffset='118.692px' fill='transparent' stroke-dasharray='565.48px'%3E%3C/circle%3E%3C/svg%3E");
    }
  }
`;
